import * as Sentry from '@sentry/vue';
import pkg  from '~/package.json';
import type { SentryServiceInterface } from './interfaces/sentry.service.interface';
import { getEnvironment } from '@/utils/environment';
import shaka from "shaka-player/dist/shaka-player.compiled.js";
import { usePlayerStore } from '@/stores/Player';

export class SentryService implements SentryServiceInterface {
    constructor(
    ) {
        this.init().catch((error) => {
            console.error('Failed to initialize Sentry:', error);
        });
    }
    private async init(): Promise<void> {
        const { vueApp, $platform, $domains } = useNuxtApp();
        const router = useRouter();
        const deviceInfo = await $platform.getDeviceInfo();
        const config = useAppConfig();

        Sentry.init({
            app: vueApp,
            dsn: import.meta.env.VITE_SENTRY_DSN,
            integrations: [
                Sentry.browserTracingIntegration({ router }),
                Sentry.replayIntegration({
                    networkDetailAllowUrls: [
                        `${config.baseUrl}/api/v3/guide/client`,
                        `${config.baseUrl}/api/v2/favorites/add`
                    ],
                    networkResponseHeaders: ['X-Trace-Id'],
                }),
            ],
            tracesSampleRate: 1.0,
            environment: getEnvironment(),
            release: pkg.version,
            tracingOptions: {
                hooks: ["mount", "update"],
                timeout: 2000,
                trackComponents: true,
            },
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0,
            trackComponents: true,
            beforeSend: async (event, hint) => {
                // Get real-time memory info at the moment of error
                try {
                    const memoryInfo = await $platform.getMemoryInfo();
                    if (memoryInfo) {
                        // Update contexts with current memory state
                        // Note: device.memory is a reserved Sentry field expecting numeric bytes,
                        // so we use a custom memoryInfo context to avoid server-side normalization to null
                        event.contexts = {
                            ...event.contexts,
                            memoryInfo: {
                                total: memoryInfo.total,
                                available: memoryInfo.available,
                                status: memoryInfo.status,
                            },
                        };
                        
                        // Add memory status as tag for easier filtering in Sentry
                        event.tags = {
                            ...event.tags,
                            memoryStatus: memoryInfo.status || 'unknown',
                        };
                    }
                } catch (memError) {
                    console.warn('Failed to get real-time memory info for Sentry:', memError);
                }

                const err = hint?.originalException;
                if (err instanceof shaka.util.Error) {
                    const PlayerStoreModule = usePlayerStore();
                    const isNotOnPlayerPage = !router.currentRoute.value.path.includes('player');

                    if (!PlayerStoreModule.isPlayerInitialized || isNotOnPlayerPage) {
                        console.warn(`Shaka error ${err.code} ignored - player not initialized (${PlayerStoreModule.isPlayerInitialized}) or not on player page (${isNotOnPlayerPage})`);
                        return null;
                    }

                    // Customize the fingerprint to include the Shaka error code
                    event.fingerprint = ["{{ default }}", `ShakaError ${err.code}`];

                    const orig = event.exception?.values?.[0];
                    // Replace the original exception with a more detailed Shaka error
                    event.exception = {
                        values: [
                            {
                                ...orig,
                                type: "ShakaError",
                                value: `Shaka error ${err.code}: ${err.message}`,
                                mechanism: {
                                    ...orig?.mechanism,
                                    type: "onunhandledrejection",
                                    handled: false,
                                    synthetic: false,
                                },
                            },
                        ],
                    };

                    // Add additional context about the Shaka error
                    event.message = `Shaka error with category(${err.category}), code(${err.code}), message(${err.message})`;
                    // Add Shaka error details as tags for easier searching
                    event.tags = {
                        ...event.tags,
                        shakaErrorSeverity: err.severity,
                        shakaErrorCategory: err.category,
                        shakaErrorCode: err.code
                    };

                    // Stringify data array items to make them visible in Sentry
                    if (err.data && Array.isArray(err.data)) {
                        // Constants for Sentry limits
                        const MAX_TAG_VALUE_LENGTH = 200; // Sentry's tag value limit
                        const MAX_CONTEXT_VALUE_LENGTH = 1000; // Reasonable limit for context
                        const MAX_TAG_ITEMS = 5; // Limit tags to prevent quota issues

                        const stringifyItem = (item: unknown, maxLength: number): string => {
                            // Handle undefined explicitly
                            if (item === undefined) {
                                return '[undefined]';
                            }
                            
                            try {
                                const stringified = JSON.stringify(item);
                                // Truncate if needed
                                return stringified.length > maxLength
                                    ? stringified.substring(0, maxLength) + '...[truncated]'
                                    : stringified;
                            } catch (e) {
                                // Fallback for circular references or stringify failures
                                console.warn('Failed to stringify Shaka error data item:', e);
                                try {
                                    // Avoid default object stringification [object Object]
                                    if (item && typeof item === 'object') {
                                        return `[${item.constructor?.name || 'Object'}]`;
                                    }
                                    const str = String(item);
                                    return str.length > maxLength
                                        ? str.substring(0, maxLength) + '...[truncated]'
                                        : str;
                                } catch {
                                    return `[${typeof item}]`;
                                }
                            }
                        };

                        // Cache stringified items to avoid duplicate work
                        const stringifiedItems = err.data.map((item) => 
                            stringifyItem(item, MAX_CONTEXT_VALUE_LENGTH)
                        );

                        // Build tags object once, then spread
                        const dataTags = stringifiedItems
                            .slice(0, MAX_TAG_ITEMS)
                            .reduce((acc, stringified, index) => {
                                // Use shorter length for tags due to Sentry limits
                                const tagValue = stringified.length > MAX_TAG_VALUE_LENGTH
                                    ? stringified.substring(0, MAX_TAG_VALUE_LENGTH) + '...'
                                    : stringified;
                                acc[`shakaErrorData_${index}`] = tagValue;
                                return acc;
                            }, {} as Record<string, string>);

                        event.tags = {
                            ...event.tags,
                            ...dataTags
                        };

                        // Add full data array to context for complete details
                        event.contexts = {
                            ...event.contexts,
                            shakaErrorData: {
                                items: stringifiedItems,
                                length: err.data.length
                            }
                        };

                        // Also add to extra for visibility in main error view
                        event.extra = {
                            ...event.extra,
                            shakaErrorDataStringified: stringifiedItems
                        };
                    }
                }
                // handle QuotaExceededError for IndexedDB
                
                if (err instanceof DOMException && err.name === 'QuotaExceededError') {
                    // Ignore these errors
                    console.warn('QuotaExceededError encountered, ignoring for Sentry reporting.');

                    $domains.idbCacher.deleteDBase().then(() => {
                        console.warn('IndexedDB database deleted due to QuotaExceededError.');
                    }).catch((e) => {
                        console.error('Error deleting IndexedDB database:', e);
                    });
                    return null;
                }

                // Handle Firebase Installations not-registered error (non-actionable, Samsung/Tizen specific)
                if (err instanceof Error && (err as { code?: string }).code === 'installations/not-registered') {
                    console.warn('Firebase installations/not-registered error ignored for Sentry reporting.', err);
                    return null;
                }

                // Suppress Firebase Installations request-failed (referer blocked) on non-production
                if (
                    err instanceof Error &&
                    (err as { code?: string }).code === 'installations/request-failed' &&
                    getEnvironment() !== 'production'
                ) {
                    console.warn('Firebase installations/request-failed error ignored for Sentry reporting (non-production).', err);
                    return null;
                }

                return event;
            },
            beforeBreadcrumb: (breadcrumb, hint) => {
                if (breadcrumb.category === 'xhr' && hint?.xhr) {
                    try {
                        const traceId: string | null = hint.xhr.getResponseHeader('X-Trace-Id');
                        if (traceId) {
                            breadcrumb.data = { ...breadcrumb.data, 'X-Trace-Id': traceId };
                        }
                    } catch {
                        console.error('X-Trace-Id not accessible: server must include Access-Control-Expose-Headers: X-Trace-Id');
                    }
                } else if (breadcrumb.category === 'fetch' && hint?.response) {
                    const traceId: string | null = hint.response.headers?.get?.('X-Trace-Id') ?? null;
                    if (traceId) {
                        breadcrumb.data = { ...breadcrumb.data, 'X-Trace-Id': traceId };
                    }
                }
                return breadcrumb;
            },
        });

        // Set device context for Sentry
        Sentry.setContext('device', {
            ...deviceInfo
        });

        // Set valuable tags for Sentry
        Sentry.setTags({
            macAddress: deviceInfo.macAddress,
            model: deviceInfo.build?.model,
            buildVersion: deviceInfo.build?.buildVersion,
        });
    };
    public setUserId(userId: string | null): void {
        // Set user context for Sentry
        Sentry.setUser({
            id: userId || 'Guest',
        });
    };
}